import { useEffect } from 'react';
import type { ShotMark } from '@studio/shared';

import { assetUrl, type GalleryEntry } from '../lib/api';
import { useT } from '../lib/i18n';

/**
 * Quick sort: the frames nobody has judged yet, one at a time, as a pile of
 * prints. Keep or cut the one on top and the next comes up; K and X do the
 * same from the keyboard. The count of what is left is the size of the job,
 * so it is the largest thing beside the pile.
 */
export function SortDeck({
  queue,
  total,
  kept,
  cut,
  onMark,
  onUndo,
  onOpen,
  onDone,
}: {
  /** The unjudged frames, in order: the first one is on top. */
  queue: GalleryEntry[];
  total: number;
  kept: number;
  cut: number;
  onMark: (shotId: string, mark: ShotMark) => void;
  /** Takes back the last decision, when there is one to take back. */
  onUndo?: () => void;
  onOpen: (entry: GalleryEntry) => void;
  onDone: () => void;
}) {
  const t = useT();
  const top = queue[0];
  const sorted = total - queue.length;

  useEffect(() => {
    if (!top) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (target?.isContentEditable) return;
      const key = event.key.toLowerCase();
      if (key === 'k') onMark(top.shot.id, 'keep');
      else if (key === 'x') onMark(top.shot.id, 'reject');
      else return;
      event.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [top, onMark]);

  return (
    <section className="sort" aria-label={t('sort.tab')}>
      <div className="sort__side">
        <div className="sort__count" aria-live="polite">
          <b className="mono">{sorted}</b>
          <span>/{total}</span>
        </div>
        <span className="faint">{t('sort.sorted')}</span>
        <div className="sort__bar" aria-hidden="true">
          <span style={{ width: `${total ? (sorted / total) * 100 : 0}%` }} />
        </div>
        <div className="row row--wrap">
          <span className="chip is-on">{t(kept === 1 ? 'sort.keptOne' : 'sort.kept', { n: kept })}</span>
          <span className="chip">{t(cut === 1 ? 'sort.cutOne' : 'sort.cut', { n: cut })}</span>
        </div>
        {onUndo && (
          <button type="button" className="btn btn--sm btn--ghost sort__undo" onClick={onUndo}>
            {t('sort.undo')}
          </button>
        )}
      </div>

      <div className="sort__stage">
        {top ? (
          <>
            <div className="sort__deck">
              {queue
                .slice(1, 3)
                .reverse()
                .map((entry, i) => (
                  <span key={entry.shot.id} className={`sort__under sort__under--${i}`} aria-hidden="true">
                    {entry.asset && <img src={assetUrl(entry.asset.id)} alt="" />}
                  </span>
                ))}
              <button
                key={top.shot.id}
                type="button"
                className={`sort__top sort__top--${sorted % 2}`}
                aria-label={t('sort.open')}
                onClick={() => onOpen(top)}
              >
                {top.asset && <img src={assetUrl(top.asset.id)} alt="" />}
              </button>
            </div>
            <div className="sort__actions">
              <button type="button" className="sort__btn" onClick={() => onMark(top.shot.id, 'reject')}>
                {t('sort.reject')} <kbd>X</kbd>
              </button>
              <button
                type="button"
                className="sort__btn sort__btn--keep"
                onClick={() => onMark(top.shot.id, 'keep')}
              >
                {t('sort.keep')} <kbd>K</kbd>
              </button>
            </div>
          </>
        ) : (
          <div className="sort__done">
            <p className="display">{t('sort.done')}</p>
            <button type="button" className="btn btn--primary" onClick={onDone}>
              {t('sort.toSheet')}
            </button>
          </div>
        )}
      </div>

      {queue.length > 1 && (
        <div className="sort__next">
          <span className="label">{t('sort.next')}</span>
          <div className="sort__queue">
            {queue.slice(1, 13).map((entry) => (
              <span key={entry.shot.id} className="sort__thumb">
                {entry.asset && <img src={assetUrl(entry.asset.id)} alt="" loading="lazy" />}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
